import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {uploadToStorage} from '@/hooks/sgsst/evidencias/use-evidence'
import type {components} from '@/lib/api-schema'

export type PerfilContratista = components['schemas']['PerfilContratistaRead']
export type PerfilContratistaLinea = components['schemas']['PerfilContratistaLineaRead']
export type ContrastePerfilContratista = components['schemas']['ContrasteRead']
export type RequisitoDelPerfil = components['schemas']['RequisitoDelPerfilRead']
export type LineaEscrita = components['schemas']['PerfilContratistaLineaWrite']

function base(businessId: string, employeeId: string) {
  return `/businesses/${businessId}/employees/${employeeId}/perfil-contratista`
}

function perfilKey(businessId: string, employeeId: string) {
  return ['perfil-contratista', businessId, employeeId]
}

/** Nunca 404 por falta de perfil: «sin perfil ocupacional definido» es un estado propio. */
export function usePerfilContratista(businessId: string | undefined, employeeId: string | undefined) {
  return useQuery({
    queryKey: perfilKey(businessId!, employeeId!),
    queryFn: async () => (await api.get<PerfilContratista>(base(businessId!, employeeId!))).data,
    enabled: !!businessId && !!employeeId,
  })
}

function useHojaMutation<TInput>(
  businessId: string,
  employeeId: string,
  ejecutar: (input: TInput) => Promise<PerfilContratista>
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ejecutar,
    onSuccess: (hoja) => {
      // Toda mutación de este paquete devuelve la hoja entera: se siembra en vez de refetchear.
      queryClient.setQueryData(perfilKey(businessId, employeeId), hoja)
    },
  })
}

/** Los bytes suben directo al almacenamiento, nunca por la API; la lectura ocurre al
 * registrar y devuelve las listas sin confirmar. */
export function useCargarPerfilEntregado(businessId: string, employeeId: string) {
  return useHojaMutation<{archivo: File} & Procedencia>(
    businessId,
    employeeId,
    async ({archivo, autorDeclarado, fechaDeclarada, vistoCargadoEn}) => {
      const destino = (
        await api.post<components['schemas']['PerfilUploadTargetRead']>(`${base(businessId, employeeId)}/uploads`, {
          filename: archivo.name,
          content_type: archivo.type,
          size_bytes: archivo.size,
        })
      ).data
      await uploadToStorage(destino, archivo, () => {})
      return (
        await api.post<PerfilContratista>(base(businessId, employeeId), {
          origen: 'perfil_contratista',
          object_key: destino.object_key,
          filename: archivo.name,
          content_type: archivo.type,
          size_bytes: archivo.size,
          autor_declarado: autorDeclarado,
          fecha_declarada: fechaDeclarada,
          visto_cargado_en: vistoCargadoEn,
        })
      ).data
    }
  )
}

/** `vistoCargadoEn` es la versión que la pantalla tenía delante: cargar reemplaza todo y
 * sin él la segunda persona pisa el trabajo de la primera. */
export type Procedencia = {
  autorDeclarado: string | null
  fechaDeclarada: string | null
  vistoCargadoEn: string | null
}

export function useEscribirPerfilAMano(businessId: string, employeeId: string) {
  return useHojaMutation<{lineas: LineaEscrita[]} & Procedencia>(
    businessId,
    employeeId,
    async ({lineas, autorDeclarado, fechaDeclarada, vistoCargadoEn}) =>
      (
        await api.post<PerfilContratista>(base(businessId, employeeId), {
          origen: 'perfil_contratista',
          lineas,
          autor_declarado: autorDeclarado,
          fecha_declarada: fechaDeclarada,
          visto_cargado_en: vistoCargadoEn,
        })
      ).data
  )
}

export function useConfirmarLineaPerfil(businessId: string, employeeId: string) {
  return useHojaMutation<string>(
    businessId,
    employeeId,
    async (lineaId) =>
      (await api.post<PerfilContratista>(`${base(businessId, employeeId)}/lineas/${lineaId}/confirmar`)).data
  )
}

/** `null` confirma todas las pendientes. */
export function useConfirmarLineasPerfil(businessId: string, employeeId: string) {
  return useHojaMutation<string[] | null>(
    businessId,
    employeeId,
    async (lineaIds) =>
      (
        await api.post<PerfilContratista>(`${base(businessId, employeeId)}/confirmar-lineas`, {
          linea_ids: lineaIds,
        })
      ).data
  )
}

/** Reescribe el texto y borra la firma: lo corregido se vuelve a confirmar. */
export function useCorregirLineaPerfil(businessId: string, employeeId: string) {
  return useHojaMutation<{lineaId: string; texto: string}>(
    businessId,
    employeeId,
    async ({lineaId, texto}) =>
      (await api.patch<PerfilContratista>(`${base(businessId, employeeId)}/lineas/${lineaId}`, {texto})).data
  )
}

export function useQuitarLineaPerfil(businessId: string, employeeId: string) {
  return useHojaMutation<string>(
    businessId,
    employeeId,
    async (lineaId) => (await api.delete<PerfilContratista>(`${base(businessId, employeeId)}/lineas/${lineaId}`)).data
  )
}

export function useContrastarPerfil(businessId: string, employeeId: string) {
  return useHojaMutation<string>(
    businessId,
    employeeId,
    async (proyectoId) =>
      (await api.post<PerfilContratista>(`${base(businessId, employeeId)}/contrastar`, {proyecto_id: proyectoId})).data
  )
}

/** Acto de firmante; devolver exige motivo y ninguno de los dos toca el contenido. */
export function useDecidirPerfilContratista(businessId: string, employeeId: string) {
  return useHojaMutation<{decision: 'aceptar' | 'devolver'; motivo?: string}>(
    businessId,
    employeeId,
    async ({decision, motivo}) =>
      (
        await api.post<PerfilContratista>(
          `${base(businessId, employeeId)}/${decision}`,
          decision === 'devolver' ? {motivo} : undefined
        )
      ).data
  )
}

export function useEscribirEmpresaContratada(businessId: string, employeeId: string) {
  return useHojaMutation<{proveedorId: string | null; empresaNombre: string | null}>(
    businessId,
    employeeId,
    async ({proveedorId, empresaNombre}) =>
      (
        await api.put<PerfilContratista>(`${base(businessId, employeeId)}/empresa-contratada`, {
          proveedor_id: proveedorId,
          empresa_nombre: empresaNombre,
        })
      ).data
  )
}
